import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { Button } from 'voyager';

import { useStartCheckout } from '@common/components/BasketDrawer/hooks/useStartCheckout';
import { useBasketHold } from '@common/hooks/useBasketHold';
import { useTranslations } from '@lang';
import { basketSelectors } from '@store/basket/selectors';

import { ScreenReaderOnly } from './Notifications.styles';
import { useDialog } from './useDialog';

const DIALOG_TOAST_ID = 'basket-hold-warning';

export const WARN_FROM_MS = 5 * 60_000;

export const CHECKOUT_CLOSES_MS = 60_000;

export const splitRemaining = (ms: number) => {
  const total = Math.max(0, Math.ceil(ms / 1000));

  return { minutes: Math.floor(total / 60), seconds: total % 60 };
};

type BasketHoldWarningProps = {
  ownsCheckout?: boolean;
};

export const BasketHoldWarning = ({ ownsCheckout = false }: BasketHoldWarningProps) => {
  const isV2 = useFeatureIsOn('feature.ecom2');
  const { t } = useTranslations();
  const basketDetails = useSelector(basketSelectors.getBasketDetails) as {
    items?: Array<{ expireAt?: string | null }>;
  } | null;
  const { show: showDialog, dismiss: dismissDialog } = useDialog();
  const { startCheckout, isStartingCheckout } = useStartCheckout();
  const { state, msRemaining, expiresAt } = useBasketHold(
    isV2 ? basketDetails?.items : undefined,
    WARN_FROM_MS
  );
  const dismissedHold = useRef<number | null>(null);

  const remaining = msRemaining ?? 0;
  const hold = expiresAt?.getTime() ?? null;
  const isWarning = isV2 && state === 'holding' && remaining <= WARN_FROM_MS;
  const offersCheckout = remaining > CHECKOUT_CLOSES_MS && !ownsCheckout;

  useEffect(() => {
    if (!isWarning) {
      dismissDialog(DIALOG_TOAST_ID);
      return;
    }

    if (dismissedHold.current === hold) return;

    const { minutes, seconds } = splitRemaining(remaining);
    const time =
      minutes > 0
        ? t('basket.holdRemainingMinutes', { minutes, seconds })
        : t('basket.holdRemainingSeconds', { seconds });

    showDialog(
      <>
        <span aria-hidden="true">{t('basket.holdWarningTitle', { time })}</span>
        <ScreenReaderOnly>{t('basket.holdWarningAlert')}</ScreenReaderOnly>
      </>,
      {
        tone: 'warning',
        id: DIALOG_TOAST_ID,
        subtitle: t('basket.holdWarningDetail'),
        onDismiss: () => {
          dismissedHold.current = hold;
          dismissDialog(DIALOG_TOAST_ID);
        },
        cta: offersCheckout ? (
          <Button
            variant="outline"
            size="small"
            disabled={isStartingCheckout}
            onClick={() => startCheckout()}
          >
            {t('basket.checkout')}
          </Button>
        ) : undefined,
      }
    );
  }, [
    isWarning,
    remaining,
    hold,
    offersCheckout,
    isStartingCheckout,
    startCheckout,
    showDialog,
    dismissDialog,
    t,
  ]);

  useEffect(() => () => dismissDialog(DIALOG_TOAST_ID), [dismissDialog]);

  return null;
};
